import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import GroupSelect from './GroupSelect';
import VariantsTable from './GroupVariantsModal/VariantsTable';
import { getVariantsByGroup, exportVariantsByGroup, importVariants, downloadVariantsTemplate } from '../api/variants';
import { VariantInterface } from '../types';
import ImportErrorModal from './ImportErrorModal';

function AllVariantsPage(): React.JSX.Element {
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [variants, setVariants] = useState<VariantInterface[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('active');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyAvailable, setOnlyAvailable] = useState(false);
  const [importErrors, setImportErrors] = useState<any[]>([]);
  const [importErrorsModalOpen, setImportErrorsModalOpen] = useState(false);


  const fetchVariants = useCallback(async (pageNum: number) => {
    if (!selectedGroupId) return;
    setLoading(true);
    try {
      let queryParams = `page=${pageNum}&page_size=${pageSize}&inactive=${!onlyActive}&unavailable=${!onlyAvailable}`;
      if (searchQuery) {
        queryParams += `&method=search&name=${encodeURIComponent(searchQuery)}`;
      }
      if (sortBy) {
        queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
      }
      const res = await getVariantsByGroup(selectedGroupId, queryParams);
      setVariants(res.data || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 1);
      setPage(pageNum);
    } finally {
      setLoading(false);
    }
  }, [selectedGroupId, pageSize, onlyActive, onlyAvailable, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchVariants(1);
    } else {
      setVariants([]);
      setTotal(0);
      setTotalPages(1);
      setPage(1);
    }
  }, [selectedGroupId, searchQuery, sortBy, sortOrder, onlyActive, onlyAvailable, fetchVariants]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchVariants(page);
    }
  }, [page, fetchVariants, selectedGroupId]);

  const handleExport = async () => {
    if (!selectedGroupId) return;
    let queryParams = `page=1&page_size=${total || 100}&inactive=${!onlyActive}&unavailable=${!onlyAvailable}`;
    if (searchQuery) {
      queryParams += `&method=search&name=${encodeURIComponent(searchQuery)}`;
    }
    if (sortBy) {
      queryParams += `&sort_by=${sortBy}&sort_order=${sortOrder}`;
    }
    await exportVariantsByGroup(selectedGroupId, queryParams);
  };

  const handleImport = async (data: any[]) => {
    try {
      await importVariants(data);
      alert('הייבוא הושלם בהצלחה!');
      refreshVariants();
    } catch (err: any) {
      if (err.errors) {
        setImportErrors(err.errors);
        setImportErrorsModalOpen(true);
      } else {
        alert(err.message || 'שגיאה בייבוא הנתונים');
      }
    }
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const handleResetSearch = () => {
    setSearchQuery('');
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const refreshVariants = () => {
    if (selectedGroupId) {
      fetchVariants(page);
    }
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>כל הקורסים</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <GroupSelect
          value={selectedGroupId}
          onChange={setSelectedGroupId}
          label="בחר קבוצה"
        />
      </Paper>
      {selectedGroupId ? (
        <>
          <VariantsTable
            variants={variants}
            onAddVariant={() => { }}
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onRefresh={refreshVariants}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            onSearch={setSearchQuery}
            onResetSearch={handleResetSearch}
            initialSearchQuery={searchQuery}
            onlyActive={onlyActive}
            setOnlyActive={setOnlyActive}
            onlyAvailable={onlyAvailable}
            setOnlyAvailable={setOnlyAvailable}
            onExport={handleExport}
            onImport={handleImport}
            onDownloadTemplate={downloadVariantsTemplate}
            loading={loading}
          />
          <ImportErrorModal
            open={importErrorsModalOpen}
            onClose={() => setImportErrorsModalOpen(false)}
            errors={importErrors}
          />
        </>
      ) : (
        <Typography variant="body1" sx={{ mt: 2 }}>אנא בחר קבוצה לצפייה בקורסים.</Typography>
      )}
    </Box>
  );
}

export default AllVariantsPage; 